import { setStorageAdapter } from './storage.adapter';
import type { StorageAdapter, StorageNamespace } from '../types/storage';
import { pwaStoragePrefix } from '../fn/pwa';

/**
 * A standard web StorageAdapter implementation for use in native PWAs or web apps.
 * It maps both 'sync' and 'local' namespaces to standard browser `localStorage`
 * by using a namespace prefix to avoid key collisions.
 */
export const webStorageAdapter: StorageAdapter = {
  get: async (namespace: StorageNamespace, keys: string[]) => {
    console.log('get', namespace, keys);
    if (namespace === 'sync') throw new Error('Sync namespace is not supported in web storage');

    if (typeof window === 'undefined' || !window.localStorage) {
      return {};
    }

    const result: Record<string, any> = {};

    for (const key of keys) {
      const storedItem = window.localStorage.getItem(`${pwaStoragePrefix}${key}`);
      if (storedItem === null) continue;
      try {
        result[key] = JSON.parse(storedItem);
      } catch {
        result[key] = storedItem;
      }
    }

    return result;
  },

  set: async (namespace: StorageNamespace, updates: Record<string, any>) => {
    console.log('set', namespace, updates);
    if (namespace === 'sync') throw new Error('Sync namespace is not supported in web storage');

    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    for (const [key, value] of Object.entries(updates)) {
      if (value === null || value === undefined) {
        window.localStorage.removeItem(`${pwaStoragePrefix}${key}`);
      } else {
        window.localStorage.setItem(`${pwaStoragePrefix}${key}`, JSON.stringify(value));
      }
    }
  },

  remove: async (namespace: StorageNamespace, key: string) => {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    const prefix = `${pwaStoragePrefix}${namespace}`;
    window.localStorage.removeItem(`${prefix}${key}`);
  },
};

export const initWebStorage = () => {
  setStorageAdapter(webStorageAdapter);
};
